import { ChevronLeft, ChevronRight } from 'lucide-react'
import type React from 'react'
import { cn } from '../../lib/utils'

interface PaginationProps {
    currentPage: number
    totalPages: number
    rowsPerPage: number
    onPageChange: (page: number) => void
    onRowsPerPageChange: (rows: number) => void
    totalItems: number
    className?: string
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    totalItems,
    className = '',
}) => {
    return (
        <div
            className={cn(
                'flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-white/40 dark:border-slate-700/40 gap-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl rounded-b-[20px]',
                className,
            )}
        >
            {/* Left Side: Rows Selector & Info */}
            <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 font-medium">
                    <span>Rows per page:</span>
                    <select
                        value={rowsPerPage}
                        onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
                        className="w-16 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-800 dark:text-gray-200 text-xs rounded-lg focus:ring-emerald-500 focus:border-emerald-500 block p-1.5 outline-none text-center font-bold cursor-pointer"
                    >
                        {[3, 4, 5, 6, 7, 8, 9, 10, 13, 15, 20, 25, 30, 35, 40].map((num) => (
                            <option key={num} value={num}>
                                {num}
                            </option>
                        ))}
                    </select>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing{' '}
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                        {Math.min((currentPage - 1) * rowsPerPage + 1, totalItems)}
                    </span>{' '}
                    -{' '}
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                        {Math.min(currentPage * rowsPerPage, totalItems)}
                    </span>{' '}
                    of <span className="font-bold text-gray-800 dark:text-gray-200">{totalItems}</span>
                </span>
            </div>

            {/* Right Side: Navigation */}
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400 transition-colors cursor-pointer disabled:pointer-events-none"
                    aria-label="Previous Page"
                >
                    <ChevronLeft size={16} />
                </button>

                <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let p = i + 1
                        if (totalPages > 5) {
                            if (currentPage > 3) p = currentPage - 2 + i
                            // Ensure we don't go out of bounds
                            if (p < 1) p = 1 // Correction
                            if (currentPage > totalPages - 2) {
                                // if current is last or second last, show last 5
                                p = totalPages - 4 + i
                            }
                        }
                        return (
                            <button
                                key={p}
                                onClick={() => onPageChange(p)}
                                className={cn(
                                    'w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all cursor-pointer',
                                    currentPage === p
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800',
                                )}
                            >
                                {p}
                            </button>
                        )
                    })}
                </div>

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400 transition-colors cursor-pointer disabled:pointer-events-none"
                    aria-label="Next Page"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    )
}
